import { defineStore } from 'pinia'
import { useAccountStore } from '@/stores/accountStore'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import type {
  IAnalysisResult,
  TAnalysisResultValue,
} from '@team-milva/lab-models/resources'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '@/firebase/init'

export type SaveAnalysisResultEntry = {
  resultID?: string
  analysisTestID: string
  result: TAnalysisResultValue
}

export type NewReportDescriptionItemEntry = {
  id: string
  reportDescriptionID: string
  text: string
  locale: string
}

export type SaveAnalysisResultsParams = {
  orderID: string
  sampleID: string
  analysisID: string
  results: SaveAnalysisResultEntry[]
  equipmentIDs?: string[]
  newReportDescriptionItems?: NewReportDescriptionItemEntry[]
  // The full intended set of turned-off tests for this analysis, not a delta —
  // anything omitted has the flag cleared server-side.
  excludedAnalysisTestIDs?: string[]
}

export const useAnalysisResultStore = defineStore('analysisResult', {
  state: () => ({
    sampleAnalysisResults: [] as IAnalysisResult[],
    unsubscribeSampleAnalysisResults: null as (() => void) | null,
    subscribedSampleID: null as string | null,
  }),

  getters: {
    sampleAnalysisResultsLoaded: (state): boolean =>
      !!state.unsubscribeSampleAnalysisResults,
  },

  actions: {
    getAnalysisResultsForSample(sampleID: string): Promise<IAnalysisResult[]> {
      if (
        this.subscribedSampleID === sampleID &&
        this.sampleAnalysisResultsLoaded
      ) {
        return Promise.resolve(this.sampleAnalysisResults)
      }

      // Unsubscribe from any previous sample before subscribing to the new one
      this.resetSampleResults()

      return new Promise((resolve) => {
        let resolved = false
        const labAccountID = useAccountStore().labAccount?.id ?? ''

        const q = query(
          collection(db, 'Apps', '-lab-customer-', 'AnalysisResults'),
          where('labAccountID', '==', labAccountID),
          where('sampleID', '==', sampleID),
          where('activeState', '==', 'active')
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.sampleAnalysisResults = querySnapshot.docs.map(
            (docSnap) => docSnap.data() as IAnalysisResult
          )

          if (!resolved) {
            resolved = true
            this.subscribedSampleID = sampleID
            resolve(this.sampleAnalysisResults)
          }
        })

        this.unsubscribeSampleAnalysisResults = unsubscribe
      })
    },

    resetSampleResults(): void {
      if (this.unsubscribeSampleAnalysisResults) {
        this.unsubscribeSampleAnalysisResults()
        this.unsubscribeSampleAnalysisResults = null
      }
      this.sampleAnalysisResults = []
      this.subscribedSampleID = null
    },

    resetStore(): void {
      this.resetSampleResults()
    },

    // Returns a map of analysisTestID -> newly created resultID
    async saveAnalysisResults(
      params: SaveAnalysisResultsParams
    ): Promise<Record<string, string>> {
      const labAccountID = useAccountStore().labAccount?.id ?? ''
      const response = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/samples/{sampleID}/analysis-results/save',
        {
          path: {
            labAccountID,
            orderID: params.orderID,
            sampleID: params.sampleID,
          },
          body: {
            analysisID: params.analysisID,
            // TODO: remove cast after deploying and re-running openapi:gen —
            // the generated codegen types still reflect the currently-deployed
            // spec's multi-select result shape (customTextListID/textListItemID).
            ...({
              results: params.results,
              newReportDescriptionItems: params.newReportDescriptionItems,
              excludedAnalysisTestIDs: params.excludedAnalysisTestIDs,
            } as object),
            equipmentIDs: params.equipmentIDs,
          },
        }
      )
      if (!response.success) {
        throw new Error(response.message ?? 'Save analysis results failed')
      }
      return (response.data?.createdIDs ?? {}) as Record<string, string>
    },

    async completeAnalysisResult(
      resultID: string,
      orderID: string,
      sampleID: string
    ): Promise<void> {
      const labAccountID = useAccountStore().labAccount?.id ?? ''
      const response = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/samples/{sampleID}/analysis-results/{resultID}/complete',
        {
          path: { labAccountID, orderID, sampleID, resultID },
        }
      )
      if (!response.success) {
        throw new Error(response.message ?? 'Complete analysis result failed')
      }
    },

    async requestAnalysisQa(
      orderID: string,
      sampleID: string,
      analysisID: string
    ): Promise<void> {
      const labAccountID = useAccountStore().labAccount?.id ?? ''
      // TODO: remove cast after deploying and re-running openapi:gen
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await (managementApi as any)(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/samples/{sampleID}/analysis-results/analysis/{analysisID}/request-qa',
        {
          path: { labAccountID, orderID, sampleID, analysisID },
        }
      )
      if (!response.success) {
        throw new Error(response.message ?? 'Request analysis QA failed')
      }
    },

    async approveAnalysisQa(
      orderID: string,
      sampleID: string,
      analysisID: string
    ): Promise<void> {
      const labAccountID = useAccountStore().labAccount?.id ?? ''
      // TODO: remove cast after deploying and re-running openapi:gen
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await (managementApi as any)(
        'post',
        'lab-accounts/{labAccountID}/orders/{orderID}/samples/{sampleID}/analysis-results/analysis/{analysisID}/approve-qa',
        {
          path: { labAccountID, orderID, sampleID, analysisID },
        }
      )
      if (!response.success) {
        throw new Error(response.message ?? 'Approve analysis QA failed')
      }
    },
  },
})

